import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import GoogleButton from "@/auth/GoogleButton";
import AppOnboarding, { shouldShowOnboarding, markOnboarded } from "@/components/AppOnboarding";
import InstallBanner from "@/components/InstallBanner";
import {
  CalendarDays, ArrowRight, Clock, Users as UsersIcon, BarChart3,
  MapPin, Umbrella, Mail, Crown, User, UserCog,
} from "lucide-react";
import { toast } from "sonner";

const DEMO = [
  { email: "eigenaar@rooster.app", password: "eigenaar123", roleKey: "role_owner", permKey: "role_perm_owner", Icon: Crown,  accent: "hsl(38 92% 50%)" },
  { email: "manager@rooster.app",  password: "manager123",  roleKey: "role_manager", permKey: "role_perm_manager", Icon: UserCog, accent: "hsl(280 65% 55%)" },
  { email: "lead@rooster.app",     password: "lead123",     roleKey: "role_teamlead", permKey: "role_perm_teamlead", Icon: UsersIcon, accent: "hsl(190 80% 42%)" },
  { email: "emma@rooster.app",     password: "emma123",     roleKey: "role_employee", permKey: "role_perm_employee", Icon: User, accent: "hsl(217 91% 60%)" },
];

// Fase 6.2 — Demo accounts blijven bewaard voor development/QA, maar mogen
// niet zichtbaar zijn op de publieke productie-loginpagina. Toon uitsluitend
// wanneer we in een dev-build draaien óf wanneer de operator een expliciete
// flag zet via REACT_APP_SHOW_DEMO=true.
// TIJDELIJK (op verzoek gebruiker, 6.5A) — hardcoded true zodat de demo-
// accounts vandaag zichtbaar zijn op /login. Terugdraaien vóór commerciële
// lancering: vervang met de env/NODE_ENV-check hieronder.
// const SHOW_DEMO_ACCOUNTS =
//   (typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production") ||
//   (typeof process !== "undefined" && process.env && process.env.REACT_APP_SHOW_DEMO === "true");
const SHOW_DEMO_ACCOUNTS = true;

// Fase 6.2 — Productwaarde-blokken. Alleen functionaliteit die de app
// vandaag daadwerkelijk levert. Geen swaps/notifs-verzinsels op de landing;
// die staan in het handboek.
const PRODUCT_VALUE = (t) => [
  { Icon: CalendarDays, title: t("pv_roster_title"),    desc: t("pv_roster_desc") },
  { Icon: Umbrella,     title: t("pv_leave_title"),     desc: t("pv_leave_desc") },
  { Icon: Clock,        title: t("pv_hours_title"),     desc: t("pv_hours_desc") },
  { Icon: UsersIcon,    title: t("pv_employees_title"), desc: t("pv_employees_desc") },
  { Icon: BarChart3,    title: t("pv_reports_title"),   desc: t("pv_reports_desc") },
  { Icon: MapPin,       title: t("pv_locations_title"), desc: t("pv_locations_desc") },
];

export default function LoginPage() {
  const { user, login, completeMfaChallenge } = useAuth();
  const { t, lang, setLang, langs } = useI18n();
  const navigate = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Phase 8.2 — MFA challenge state. `mfaCtx=null` means we're still
  // on the password screen. Otherwise it carries `{challenge_id}` and
  // the SPA renders the code entry step.
  const [mfaCtx, setMfaCtx] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaUseRecovery, setMfaUseRecovery] = useState(false);
  // Show the PWA-only onboarding slides on first launch (state lifted so the
  // "Aan de slag" button can dismiss the overlay without a route change).
  const [showOnboarding, setShowOnboarding] = useState(() => shouldShowOnboarding());

  useEffect(() => {
    if (user && user !== null) {
      // Any successful auth means the user has completed onboarding — mark it.
      markOnboarded();
      const dest = ["owner", "manager", "teamlead"].includes(user.role) ? "/dashboard" : "/my-roster";
      navigate(loc.state?.from || dest, { replace: true });
    }
  }, [user, loc.state, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const res = await login(email, password);
    setBusy(false);
    if (res.mfa_required) {
      setMfaCtx({ challenge_id: res.challenge_id });
      return;
    }
    if (!res.ok) { setError(res.error); toast.error(res.error); }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    if (!mfaCtx?.challenge_id) return;
    setBusy(true); setError("");
    const res = await completeMfaChallenge(mfaCtx.challenge_id, mfaCode, mfaUseRecovery);
    setBusy(false);
    if (!res.ok) {
      if (res.rate_limited) {
        setError(t("login_mfa_rate_limited"));
        toast.error(t("login_mfa_rate_limited"));
      } else {
        setError(t("login_mfa_error"));
        toast.error(t("login_mfa_error"));
      }
      setMfaCode("");
    }
  };

  const applyDemo = (d) => { setEmail(d.email); setPassword(d.password); };

  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] relative overflow-hidden">
      {/* PWA first-launch slides — only in standalone mode, only once */}
      {showOnboarding && (
        <AppOnboarding onDone={() => setShowOnboarding(false)} />
      )}
      {/* Install-as-app prompt — only in mobile browser (not PWA) */}
      <InstallBanner />
      {/* Soft ambient background: keep the mesh, drop the busy grid backdrop
          for something that reads as editorial rather than "AI landing" */}
      <div className="hero-mesh" aria-hidden />
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.35]"
        aria-hidden
        style={{
          backgroundImage: "radial-gradient(hsl(var(--border) / 0.9) 1px, transparent 1px)",
          backgroundSize: "22px 22px",
          maskImage: "radial-gradient(ellipse at 50% 20%, black 20%, transparent 65%)",
          WebkitMaskImage: "radial-gradient(ellipse at 50% 20%, black 20%, transparent 65%)",
        }}
      />
      {/* Phase 9B.3b7 — dark auth palette correction. The prior amber/orange
          radial blobs made the login feel like a warm hotel lobby; the user
          rejected that direction. Now a cool graphite dark surface with a
          single restrained primary-blue accent that matches the rest of
          Rostr. Light mode keeps its calm neutral treatment untouched. */}
      <div
        className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full pointer-events-none blur-3xl opacity-30 dark:opacity-45"
        aria-hidden
        style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.35), transparent 65%)" }}
      />
      <div
        className="absolute -bottom-52 -left-40 w-[560px] h-[560px] rounded-full pointer-events-none blur-3xl opacity-0 dark:opacity-25"
        aria-hidden
        style={{ background: "radial-gradient(circle, hsl(217 60% 45% / 0.35), transparent 70%)" }}
      />

      {/* Top nav */}
      <nav className="relative z-10 max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-10 py-6">
        <Link to="/login" className="flex items-center gap-2.5 group">
          <span className="font-display text-[20px] font-bold tracking-tight text-[hsl(var(--text-strong))]">Rostr</span>
        </Link>
        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center gap-6 mr-4 text-[13px] text-[hsl(var(--text-muted))]">
            <a href="#features" className="hover:text-[hsl(var(--text))] transition-colors">{t("nav_features")}</a>
            <a href="#faq" className="hover:text-[hsl(var(--text))] transition-colors">{t("nav_faq")}</a>
          </div>
          <select
            data-testid="login-lang" value={lang} onChange={(e) => setLang(e.target.value)}
            className="input-field !h-9 !text-xs !w-auto"
          >
            {langs.map((l) => <option key={l.code} value={l.code}>{l.flag} {l.code.toUpperCase()}</option>)}
          </select>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-6 pb-20 grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-16 items-center">
        <div className="max-w-xl">
          <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[hsl(var(--primary))] mb-5"
          >
            {t("landing_eyebrow") || "Werkplanning · voor teams die 't rustig willen"}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
            className="font-display text-[34px] sm:text-[40px] md:text-[52px] lg:text-[60px] leading-[1.02] tracking-[-0.02em] text-[hsl(var(--text-strong))] break-words"
          >
            {t("hero_line1")}
            <br />
            <span className="relative inline-block">
              {t("hero_line2")}
              {/* Underline accent — consistent primary-blue in light and dark. */}
              <span
                aria-hidden
                className="absolute left-0 right-0 -bottom-1 h-[6px] rounded-full opacity-70 bg-[linear-gradient(90deg,_hsl(var(--primary)/0.6),_hsl(199_89%_48%/0.15))]"
              />
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-6 text-[16.5px] leading-[1.65] text-[hsl(var(--text-muted))] max-w-[36ch]"
          >
            {t("hero_desc")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <Link to="/create-company" data-testid="cta-create-company" className="btn btn-primary !h-12 !px-6 !text-[14.5px] group">
              {t("create_company_cta")}
              <ArrowRight size={16} strokeWidth={2.2} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link to="/join" data-testid="cta-join-company" className="text-[14px] font-semibold text-[hsl(var(--text))] hover:text-[hsl(var(--primary))] transition-colors underline underline-offset-[6px] decoration-[hsl(var(--border-strong))] decoration-[1.5px] hover:decoration-[hsl(var(--primary))] px-2 h-12 inline-flex items-center">
              {t("have_invite_cta")}
            </Link>
          </motion.div>

          {/* Fase 6.2 — Social proof met "200+ teams" en fake avatar-stack
              is verwijderd. We hebben op dit moment geen recht om die claim te
              doen; de landing verwijst nu direct naar de productwaarde-sectie
              hieronder. */}
        </div>

        {/* Login card */}
        <motion.div
          id="login"
          initial={{ opacity: 0, y: 20, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
          className="card !rounded-[24px] p-7 md:p-9 relative"
          style={{ boxShadow: "0 30px 80px -30px hsl(222 47% 11% / 0.18), 0 6px 20px -12px hsl(222 47% 11% / 0.08)" }}
        >
          <div className="mb-6">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[hsl(var(--text-muted))] mb-2">
              {t("login")}
            </div>
            <h2 className="font-display text-[26px] leading-[1.05] tracking-tight text-[hsl(var(--text-strong))]">
              {t("login_title")}
            </h2>
            <p className="text-[13.5px] text-[hsl(var(--text-muted))] mt-1.5 leading-relaxed">
              {t("login_subtitle")}
            </p>
          </div>

          <form onSubmit={mfaCtx ? submitMfa : submit} className="flex flex-col gap-3.5" data-testid="login-form">
            {!mfaCtx && <GoogleButton intent="login" dataTestid="login-google" />}
            {!mfaCtx && (
              <div className="flex items-center gap-3 my-1">
                <div className="flex-1 h-px bg-[hsl(var(--border))]" />
                <span className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--text-muted))]">{t("of_with_email")}</span>
                <div className="flex-1 h-px bg-[hsl(var(--border))]" />
              </div>
            )}
            {!mfaCtx ? (
              <>
                <label className="flex flex-col gap-1.5">
                  <span className="text-[11.5px] font-semibold text-[hsl(var(--text))] uppercase tracking-[0.08em]">{t("email")}</span>
                  <input
                    data-testid="login-email" type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-field"
                    placeholder="jij@rostr.app" autoComplete="email"
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-[11.5px] font-semibold text-[hsl(var(--text))] uppercase tracking-[0.08em]">{t("password")}</span>
                  <input
                    data-testid="login-password" type="password" required value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field"
                    placeholder="••••••••" autoComplete="current-password"
                  />
                </label>
              </>
            ) : (
              // Phase 8.2 — MFA challenge step. Renders in-place inside the
              // existing login card so the same layout carries through.
              <div className="flex flex-col gap-3" data-testid="login-mfa-step">
                <div>
                  <div className="text-[11.5px] font-semibold text-[hsl(var(--text))] uppercase tracking-[0.08em] mb-1">
                    {t("login_mfa_title")}
                  </div>
                  <p className="text-[13px] text-[hsl(var(--text-muted))] mb-2 leading-relaxed">
                    {mfaUseRecovery ? t("mfa_recovery_title") : t("login_mfa_subtitle")}
                  </p>
                </div>
                <input
                  key={mfaUseRecovery ? "recovery" : "totp"}
                  data-testid="login-mfa-code"
                  autoFocus
                  inputMode={mfaUseRecovery ? "text" : "numeric"}
                  autoComplete="one-time-code"
                  maxLength={mfaUseRecovery ? 12 : 6}
                  value={mfaCode}
                  onChange={(e) => {
                    const v = mfaUseRecovery
                      ? e.target.value.toUpperCase().slice(0, 12)
                      : e.target.value.replace(/\D/g, "").slice(0, 6);
                    setMfaCode(v);
                  }}
                  className="input-field text-center text-xl tracking-widest font-mono"
                  placeholder={mfaUseRecovery ? t("login_mfa_recovery_placeholder") : t("login_mfa_placeholder")}
                />
                <button
                  type="button"
                  className="text-[12px] font-semibold text-[hsl(var(--text-muted))] hover:text-[hsl(var(--primary))] self-start"
                  onClick={() => { setMfaUseRecovery((r) => !r); setMfaCode(""); }}
                  data-testid="login-mfa-toggle-recovery"
                >
                  {mfaUseRecovery ? t("login_mfa_use_totp") : t("login_mfa_use_recovery")}
                </button>
              </div>
            )}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="text-xs text-[hsl(var(--danger))] font-medium bg-[hsl(var(--danger-soft))] px-3 py-2 rounded-lg"
                  data-testid="login-error"
                >{error}</motion.div>
              )}
            </AnimatePresence>
            <button type="submit" disabled={busy} data-testid={mfaCtx ? "login-mfa-submit" : "login-submit"} className="btn btn-primary !h-12 mt-1 !text-[14.5px] group">
              {busy ? t("logging_in") : (mfaCtx ? t("login_mfa_submit") : t("login"))}
              {!busy && <ArrowRight size={16} strokeWidth={2.2} className="transition-transform group-hover:translate-x-0.5" />}
            </button>
          </form>

          {/* Demo accounts — verborgen in productie (Fase 6.2). Alleen zichtbaar
              in development of achter REACT_APP_SHOW_DEMO=true. */}
          {SHOW_DEMO_ACCOUNTS && (
          <div className="mt-6 pt-6 border-t hairline" data-testid="demo-accounts-section">
            <div className="flex items-baseline justify-between mb-3">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--text-muted))]">
                {t("demo_accounts")}
              </div>
              <div className="text-[10.5px] text-[hsl(var(--text-soft))] italic">
                {t("demo_hint") || "klik om in te vullen"}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {DEMO.map((d) => (
                <button
                  key={d.email} type="button" data-testid={`demo-${d.roleKey}`} onClick={() => applyDemo(d)}
                  className="group text-left px-2.5 py-2 rounded-xl hover:bg-[hsl(var(--muted))] transition-colors border hairline flex items-center gap-2.5"
                  title={d.email}
                >
                  <span
                    className="w-8 h-8 rounded-lg grid place-items-center shrink-0 text-white shadow-[0_2px_6px_-2px_rgba(0,0,0,0.2)]"
                    style={{ background: d.accent }}
                  >
                    <d.Icon size={14} strokeWidth={2} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[12px] font-semibold text-[hsl(var(--text-strong))] leading-tight truncate">{t(d.roleKey)}</span>
                    <span className="block text-[10.5px] text-[hsl(var(--text-muted))] truncate mt-0.5">{t(d.permKey)}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
          )}
        </motion.div>
      </section>

      {/* Fase 6.2 — Productwaarde: 6 rustige blokken voor functies die
          Rostr vandaag daadwerkelijk levert. Geen verzonnen features. */}
      <section id="features" className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-16 border-t hairline">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="eyebrow mb-3">{t("landing_product_eyebrow")}</div>
          <h2 className="font-display text-[36px] md:text-[44px] leading-[1.05] tracking-tight">{t("landing_product_title")}</h2>
          <p className="mt-3 text-[16px] text-[hsl(var(--text-muted))]">{t("landing_product_desc")}</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="landing-product-value">
          {PRODUCT_VALUE(t).map((h, i) => (
            <motion.div
              key={h.title} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }} viewport={{ once: true }}
              className="card card-lift p-6"
              data-testid={`pv-block-${i}`}
            >
              <div className="w-10 h-10 rounded-xl bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] grid place-items-center mb-4">
                <h.Icon size={18} strokeWidth={1.75} />
              </div>
              <div className="font-display text-[18px] font-bold">{h.title}</div>
              <div className="text-[14px] text-[hsl(var(--text-muted))] mt-1.5 leading-relaxed">{h.desc}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="relative z-10 max-w-3xl mx-auto px-6 lg:px-10 py-16 border-t hairline">
        <div className="text-center mb-10">
          <div className="eyebrow mb-3">{t("faq")}</div>
          <h2 className="font-display text-[32px] md:text-[38px] leading-[1.05] tracking-tight">{t("faq_title")}</h2>
        </div>
        <div className="flex flex-col gap-3">
          {[
            { q: t("faq_q1"), a: t("faq_a1") },
            { q: t("faq_q2"), a: t("faq_a2") },
            { q: t("faq_q3"), a: t("faq_a3") },
            { q: t("faq_q4"), a: t("faq_a4") },
            { q: t("faq_q5"), a: t("faq_a5") },
          ].map((it, i) => (
            <details key={i} className="card p-5 group open:shadow-[var(--shadow-md)] transition-shadow">
              <summary className="cursor-pointer list-none flex items-center justify-between gap-4 text-[15px] font-semibold text-[hsl(var(--text-strong))]">
                {it.q}
                <span className="w-6 h-6 rounded-full grid place-items-center bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <div className="mt-3 text-[14px] text-[hsl(var(--text-muted))] leading-relaxed">{it.a}</div>
            </details>
          ))}
        </div>
      </section>

      {/* Support / Extra questions */}
      <section id="support" className="relative z-10 max-w-3xl mx-auto px-6 lg:px-10 pb-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }} viewport={{ once: true }}
          className="card p-6 md:p-7 flex items-start gap-4"
          data-testid="login-support-card"
          style={{
            background:
              "linear-gradient(135deg, hsl(var(--primary-soft)) 0%, hsl(var(--surface)) 60%)",
          }}
        >
          <div
            className="w-11 h-11 rounded-2xl grid place-items-center shrink-0 text-white"
            style={{
              background: "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(217 91% 45%) 100%)",
              boxShadow: "0 10px 24px -8px hsl(var(--primary) / 0.55)",
            }}
          >
            <Mail size={18} strokeWidth={2.2} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="eyebrow mb-1.5">{t("login_extra_eyebrow")}</div>
            <div className="font-display text-[18px] md:text-[20px] font-bold tracking-tight text-[hsl(var(--text-strong))]">
              {t("login_extra_title")}
            </div>
            <p className="mt-1 text-[13.5px] leading-relaxed text-[hsl(var(--text-muted))] max-w-xl">
              {t("login_extra_desc")}
            </p>
            <a
              href="mailto:rosterhelp@gmail.com"
              data-testid="login-support-email"
              className="mt-3 inline-flex items-center gap-1.5 h-10 px-4 rounded-full font-semibold text-[13px] text-white transition-transform hover:scale-[1.02]"
              style={{
                background: "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(217 91% 45%) 100%)",
                boxShadow: "0 10px 24px -8px hsl(var(--primary) / 0.55)",
              }}
            >
              <Mail size={13} strokeWidth={2.2} />
              rosterhelp@gmail.com
            </a>
          </div>
        </motion.div>
      </section>

      <footer className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-10 border-t hairline flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[hsl(var(--text-muted))]">
        <div>© {new Date().getFullYear()} Rostr — {t("footer_tagline")}</div>
        <div className="flex items-center gap-5" data-testid="landing-footer">
          <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-[hsl(var(--text))]">{t("footer_privacy")}</Link>
          <Link to="/terms" data-testid="footer-terms-link" className="hover:text-[hsl(var(--text))]">{t("footer_terms")}</Link>
          <a href="mailto:rosterhelp@gmail.com" data-testid="footer-support-link" className="hover:text-[hsl(var(--text))]">{t("footer_support")}</a>
        </div>
      </footer>
    </div>
  );
}
